import { SignUpButton, useAuth, useClerk } from "@clerk/clerk-react";
import { toast } from "sonner";
import { useSubscription } from "@/context/SubscriptionContext";
import createCheckoutSession from "@/utils/createCheckoutSession";
import { PLANS } from "@/constants/plans";
import { CheckCircle2, Zap, Star, Building2, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Section } from "@/layouts/Section";
import { PricingCard } from "@/components/marketing/PricingCard";

const PLAN_META = {
  free:       { icon: Star,      label: "Get started" },
  individual: { icon: Zap,       label: "Get Individual" },
  school:     { icon: Building2, label: "Set up a school" },
};

export default function Pricing() {
  const { planName } = useSubscription();
  const { getToken, isSignedIn } = useAuth();
  const { openSignIn } = useClerk();

  const handleUpgrade = async (priceId) => {
    if (!isSignedIn) {
      toast.error("You must create an account first");
      openSignIn();
      return;
    }
    const token = await getToken();
    await createCheckoutSession(priceId, token);
  };

  return (
    <div className="pricing-page">

      {/* ── Header ──────────────────────────────────────────────── */}
      <Section variant="light">
        <div className="max-w-[640px] mx-auto">
          <div className="text-xs font-bold tracking-[0.1em] uppercase text-primary mb-3">Plans &amp; Pricing</div>
          <h1 className="font-display text-[clamp(1.75rem,3vw,2.5rem)] font-normal text-foreground m-0 mb-4 leading-tight">
            Simple pricing for every classroom.
          </h1>
          <p className="text-[1.0625rem] leading-[1.65] text-muted-foreground m-0 max-w-[520px]">
            Start free - no credit card required. Upgrade when you need more
            reports or AI generations. Free plan resets every 30 days; paid plans reset annually.
          </p>
        </div>
      </Section>

      {/* ── Cards ───────────────────────────────────────────────── */}
      <Section variant="muted">
        <div className="max-w-[1100px] mx-auto">
          <div className="grid grid-cols-3 gap-6 items-start max-[800px]:grid-cols-1 max-[800px]:max-w-[420px] max-[800px]:mx-auto">
            {PLANS.map((plan) => {
              const meta      = PLAN_META[plan.planKey];
              const Icon      = meta.icon;
              const isCurrent = planName === plan.planKey;

              let cta;
              if (isCurrent) {
                cta = (
                  <div className={`flex items-center justify-center gap-2 px-4 py-[0.6rem] rounded-lg text-[0.9rem] font-semibold${plan.highlight ? " bg-brand-400/20 text-brand-300" : " bg-brand-700/[0.08] text-brand-700"}`}>
                    <CheckCircle2 size={15} />
                    Current plan
                  </div>
                );
              } else if (plan.stripePlanKey) {
                cta = (
                  <button
                    className={`${plan.highlight ? "inline-flex items-center gap-2 px-6 py-3 bg-brand-800 text-white text-[0.9375rem] font-semibold rounded-lg cursor-pointer transition-[background,transform] duration-150 hover:bg-brand-700 hover:-translate-y-px no-underline whitespace-nowrap" : "inline-flex items-center gap-[0.4rem] px-5 py-[0.72rem] bg-transparent text-foreground text-[0.9375rem] font-medium border border-border rounded-lg cursor-pointer transition-[border-color,background,transform] duration-150 hover:border-foreground hover:-translate-y-px no-underline whitespace-nowrap"} w-full justify-center`}
                    onClick={() => handleUpgrade(plan.stripePlanKey)}
                  >
                    {meta.label} <ArrowRight size={14} />
                  </button>
                );
              } else if (plan.ctaPath) {
                cta = (
                  <Link to={plan.ctaPath}>
                    <button className="inline-flex items-center gap-[0.4rem] px-5 py-[0.72rem] bg-transparent text-foreground text-[0.9375rem] font-medium border border-border rounded-lg cursor-pointer transition-[border-color,background,transform] duration-150 hover:border-foreground hover:-translate-y-px no-underline whitespace-nowrap w-full justify-center">
                      {meta.label} <ArrowRight size={14} />
                    </button>
                  </Link>
                );
              } else {
                cta = (
                  <SignUpButton mode="modal" afterSignInUrl="/dashboard" afterSignUpUrl="/dashboard">
                    <button className="inline-flex items-center gap-[0.4rem] px-5 py-[0.72rem] bg-transparent text-foreground text-[0.9375rem] font-medium border border-border rounded-lg cursor-pointer transition-[border-color,background,transform] duration-150 hover:border-foreground hover:-translate-y-px no-underline whitespace-nowrap w-full justify-center">
                      {meta.label} <ArrowRight size={14} />
                    </button>
                  </SignUpButton>
                );
              }

              return (
                <PricingCard
                  key={plan.planKey}
                  name={plan.name}
                  price={plan.price}
                  period={plan.period}
                  icon={Icon}
                  highlight={plan.highlight}
                  features={plan.features}
                  cta={cta}
                />
              );
            })}
          </div>
        </div>
      </Section>

      {/* ── FAQ strip ───────────────────────────────────────────── */}
      <Section variant="light">
        <div className="max-w-[900px] mx-auto">
          <div className="text-xs font-bold tracking-[0.1em] uppercase text-primary mb-3">Common questions</div>
          <h2 className="font-display text-[clamp(1.75rem,3vw,2.5rem)] font-normal text-foreground m-0 mb-8 leading-tight">
            Good to know before you sign up.
          </h2>
          <div className="grid grid-cols-2 gap-x-12 gap-y-8 max-[680px]:grid-cols-1 max-[680px]:gap-y-7">
            {FAQ.map((item) => (
              <div key={item.q} className="border-t border-border pt-5">
                <h3 className="text-[0.9375rem] font-semibold text-foreground m-0 mb-2">{item.q}</h3>
                <p className="text-[0.9rem] leading-[1.65] text-muted-foreground m-0">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── Bottom CTA ──────────────────────────────────────────── */}
      <Section variant="dark">
        <div className="max-w-[1100px] mx-auto text-center">
          <div className="text-xs font-bold tracking-[0.1em] uppercase text-brand-300 mb-3">Get started today</div>
          <h2 className="font-display text-[clamp(1.75rem,3vw,2.5rem)] font-normal text-white m-0 mb-12 leading-tight">
            Try it free - no account needed.
          </h2>
          <div className="flex flex-wrap gap-3 items-center justify-center">
            <Link to="/pgrc">
              <button className="inline-flex items-center gap-2 px-6 py-3 bg-brand-800 text-white text-[0.9375rem] font-semibold rounded-lg cursor-pointer transition-[background,transform] duration-150 hover:bg-brand-700 hover:-translate-y-px no-underline whitespace-nowrap">
                Try a free report card <ArrowRight size={16} />
              </button>
            </Link>
          </div>
        </div>
      </Section>

    </div>
  );
}

const FAQ = [
  {
    q: "What counts as a 'report card'?",
    a: "Each student report you create counts as one. Editing and saving an existing report does not use an additional slot.",
  },
  {
    q: "When does my usage reset?",
    a: "The Individual and School plan resets annually so you have 120 report cards every year. It's more than enough for one teacher.",
  },
  {
    q: "Can I share a class with a colleague?",
    a: "Yes. You can share any class with any EasyReports user and give them view or edit access. Shared classes only count against the owner's quota, not for the user you share it with",
  },
  {
    q: "What happens when my paid plan expires?",
    a: "Your plan expires after one year. Your data stays intact. You wont be able to create new reports until you renew your subscription.",
  },
  {
    q: "Is there a free trial for paid plans?",
    a: "The Free plan is a permanent free tier - 20 reports and 10 AI generations every 30 days. No trial period needed; upgrade only when you outgrow it.",
  },
  {
    q: "What report card types are supported?",
    a: "From Progress Reports to Provincial Reports to Highschool and more. Take a look by opening a free account. We keep adding more report types depending on what our users need.",
  },
];
